'use client';

import React, { useState, useEffect } from 'react';
import { fetchCashToCash } from '../../apiHelper';
import { TrendingDown, AlertTriangle, RefreshCw } from 'lucide-react';

const formatCurrency = (val) => {
    if (!val) return '₦0';
    if (val >= 1000000) return `₦${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `₦${(val / 1000).toFixed(1)}K`;
    return `₦${val.toLocaleString()}`;
};

const CashToCashDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [interestRate, setInterestRate] = useState(0.10);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const result = await fetchCashToCash({ interest_rate: interestRate });
                setData(result);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [interestRate]);

    const shortest = data?.shortest_cycle_sku_vendor || {};
    const totalLazy = data?.total_lazy_capital || 0;

    return (
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900">Cash-to-Cash Cycle</h2>
                    <p className="text-gray-500 text-xs mt-1">Lazy Capital • Opportunity Cost</p>
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-gray-50 px-5 py-3 rounded-2xl">
                        <label className="text-sm text-gray-500">Interest Rate:</label>
                        <select
                            value={interestRate}
                            onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                            className="bg-transparent font-medium outline-none"
                        >
                            <option value={0.08}>8%</option>
                            <option value={0.10}>10%</option>
                            <option value={0.12}>12%</option>
                            <option value={0.15}>15%</option>
                        </select>
                    </div>

                    <button
                        onClick={() => window.location.reload()}
                        className="px-6 py-3 bg-[#4285F4] text-white rounded-2xl hover:bg-blue-600 transition font-medium flex items-center gap-2"
                    >
                        <RefreshCw size={18} /> Refresh
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <div className="bg-gradient-to-br from-amber-600 to-orange-600 text-white p-7 rounded-3xl">
                    <p className="text-amber-100 text-sm font-medium">LAZY CAPITAL</p>
                    <p className="text-4xl font-bold mt-3 tracking-tighter">
                        {formatCurrency(totalLazy)}
                    </p>
                    <p className="text-xs text-amber-200 mt-6">Tied up in inventory</p>
                </div>

                <div className="bg-white border border-gray-200 p-7 rounded-3xl">
                    <p className="text-gray-500 text-sm font-medium">ANNUAL LOST INTEREST</p>
                    <p className="text-4xl font-bold mt-3 tracking-tighter text-red-600">
                        {formatCurrency(data?.annual_lost_compounding_interest || 0)}
                    </p>
                    <p className="text-xs text-gray-400 mt-6">at {interestRate * 100}% opportunity cost</p>
                </div>

                <div className="bg-white border border-gray-200 p-7 rounded-3xl flex flex-col justify-between">
                    <div>
                        <p className="text-gray-500 text-sm font-medium">SHORTEST CYCLE</p>
                        <p className="text-4xl font-bold mt-3 tracking-tighter text-emerald-600">
                            {shortest.avg_days_held || 0} <span className="text-2xl">days</span>
                        </p>
                    </div>
                    <div className="text-sm text-gray-600">
                        {shortest.sku} • {shortest.vendor_name}
                    </div>
                </div>
            </div>

            {/* Recommendation */}
            {data?.recommendation && (
                <div className="mb-8 bg-amber-50 border border-amber-200 rounded-3xl p-6">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="text-amber-600 mt-1" size={24} />
                        <div>
                            <h4 className="font-semibold text-amber-800">Actionable Insight</h4>
                            <p className="text-amber-700 mt-2 leading-relaxed">
                                {data.recommendation}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* All Combinations Table */}
            <div className="flex-1 bg-white border border-gray-100 rounded-3xl p-8 overflow-auto">
                <h3 className="font-semibold mb-6">All SKU-Vendor Combinations</h3>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b">
                            <th className="text-left py-4 font-medium text-gray-500">SKU</th>
                            <th className="text-left py-4 font-medium text-gray-500">Vendor</th>
                            <th className="text-right py-4 font-medium text-gray-500">Days Held</th>
                            <th className="text-right py-4 font-medium text-gray-500">Inventory Value</th>
                            <th className="text-right py-4 font-medium text-gray-500">Quantity</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data?.all_combinations?.map((item, i) => (
                            <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                                <td className="py-4 font-medium">{item.sku}</td>
                                <td className="py-4 text-gray-600">{item.vendor_name}</td>
                                <td className="py-4 text-right font-semibold">{item.avg_days_held}</td>
                                <td className="py-4 text-right">{formatCurrency(item.inventory_value)}</td>
                                <td className="py-4 text-right text-gray-600">{item.current_quantity}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CashToCashDashboard;